import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AtSign, Eraser, History, Loader2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { RelativeTime } from '@/components/DataDisplay'
import { CardBackLink, Body, ActionCard, ReasonInput } from './userActionKit'
import {
  USERS_STR as S,
  fetchProfileHistory,
  forceUsername,
  scrubUserProfile,
  isPending,
  type ProfileChange,
  type ScrubField,
} from './usersAdminApi'

/**
 * A3.08 — Profil tarixi (nom/username/avatar o'zgarishlari). Greenfield GET →
 * endpoint chiqmaguncha halol bo'sh holat (fail-soft). Read-only.
 */
export function ProfileHistory() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const [items, setItems] = useState<ProfileChange[] | null>(null)

  const load = useCallback(() => {
    const ac = new AbortController()
    setItems(null)
    fetchProfileHistory(userId, ac.signal)
      .then(setItems)
      .catch(() => setItems([]))
    return () => ac.abort()
  }, [userId])

  useEffect(() => load(), [load])

  return (
    <>
      <PageHeader title={S.histTitle} screenId="A3.08" actions={<CardBackLink id={id} />} />
      <Body>
        <p className="text-xs text-tertiary">{S.histHint}</p>
        {items === null ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : items.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">
            <History size={24} className="mx-auto mb-2 opacity-60" />
            {S.empty}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border-default">
            {items.map((c, i) => (
              <div
                key={c.id}
                className={`grid grid-cols-[7rem_1fr_1fr_auto] items-center gap-2 px-3 py-2 text-sm ${
                  i > 0 ? 'border-t border-border-subtle' : ''
                }`}
              >
                <span className="font-mono text-xs text-secondary">{c.field}</span>
                <span className="truncate text-tertiary line-through">{c.old_value ?? '—'}</span>
                <span className="truncate text-primary">{c.new_value ?? '—'}</span>
                <span className="shrink-0 text-xs text-tertiary">
                  <RelativeTime value={c.created_at} />
                </span>
              </div>
            ))}
          </div>
        )}
      </Body>
    </>
  )
}

/**
 * A3.32 — Username majburan o'zgartirish. Nomaqbul/impersonatsiya @username'ni
 * bo'shatish yoki almashtirish. Greenfield POST (`/admin/users/:id/username`) →
 * 404 bo'lsa «kutilmoqda». Bo'sh yangi-username = faqat bo'shatish.
 */
export function UsernameChange() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const { toast } = useToast()
  const [username, setUsername] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const canSubmit = reason.trim().length >= 3 && !busy

  const submit = async () => {
    if (!canSubmit) return
    setBusy(true)
    try {
      await forceUsername(userId, username, reason.trim())
      setUsername('')
      toast(S.done, { tone: 'success' })
    } catch (e) {
      if (isPending(e)) toast(S.pending, { tone: 'warning' })
      else toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.unameTitle} screenId="A3.32" actions={<CardBackLink id={id} />} />
      <Body>
        <ActionCard hint={S.unameHint}>
          <label className="block">
            <span className="mb-1 block text-xs text-tertiary">{S.unameNew}</span>
            <div className="flex items-center rounded-md border border-border-default bg-surface-1 px-2.5 focus-within:border-border-strong">
              <AtSign size={14} className="text-tertiary" />
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                className="w-full bg-transparent px-1.5 py-1.5 text-sm text-primary outline-none"
              />
            </div>
          </label>
          <ReasonInput value={reason} onChange={setReason} />
          <button
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <AtSign size={14} />}
            {S.unameApply}
          </button>
        </ActionCard>
      </Body>
    </>
  )
}

/**
 * A3.33 — Avatar/nom o'chirish. Nomaqbul avatar yoki ko'rsatiladigan nomni
 * tozalash (moderatsiya). Greenfield POST (`/admin/users/:id/scrub`).
 */
export function AvatarScrub() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const { toast } = useToast()
  const [avatar, setAvatar] = useState(true)
  const [name, setName] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const fields: ScrubField[] = [...(avatar ? ['avatar' as const] : []), ...(name ? ['name' as const] : [])]
  const canSubmit = fields.length > 0 && reason.trim().length >= 3 && !busy

  const submit = async () => {
    if (!canSubmit) {
      if (fields.length === 0) toast(S.scrubPick, { tone: 'warning' })
      return
    }
    setBusy(true)
    try {
      await scrubUserProfile(userId, fields, reason.trim())
      toast(S.done, { tone: 'success' })
    } catch (e) {
      if (isPending(e)) toast(S.pending, { tone: 'warning' })
      else toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.scrubTitle} screenId="A3.33" actions={<CardBackLink id={id} />} />
      <Body>
        <ActionCard hint={S.scrubHint}>
          <label className="flex items-center gap-2 text-sm text-secondary">
            <input type="checkbox" checked={avatar} onChange={(e) => setAvatar(e.target.checked)} />
            {S.scrubAvatar}
          </label>
          <label className="flex items-center gap-2 text-sm text-secondary">
            <input type="checkbox" checked={name} onChange={(e) => setName(e.target.checked)} />
            {S.scrubName}
          </label>
          <ReasonInput value={reason} onChange={setReason} />
          <button
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-4 py-1.5 text-sm text-danger hover:bg-danger hover:text-white disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Eraser size={14} />}
            {S.scrubApply}
          </button>
        </ActionCard>
      </Body>
    </>
  )
}
